import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, filter, merge } from 'rxjs';

import { BaseComponent } from 'src/app/base/base.component';
import { FtpServerType, FtpServerTypeUtil, FtpService } from 'src/app/domainObjects/dataConnector';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { OrganizationService } from 'src/app/views/organization/organization.service';
import {
  ftpHostValidator,
  noInnerWhitespaceValidator,
  portValidator,
  trimmedRequiredValidator
} from '../ftp-service.validators';
import {
  FTP_CONNECTION_TIMEOUT,
  FTP_DEFAULT_PORTS,
  FTP_FIELD_MAX_LENGTH,
  OrgFtpService,
  OrgFtpServiceSaveRequest,
  toFtpApiError
} from '../org-ftp-service.model';

export interface FtpServiceDialogData {
  /** Omitted to add a new service. */
  service?: OrgFtpService;
}

export interface FtpServiceDialogResult {
  message: string;
}

@Component({
  selector: 'app-ftp-service-dialog',
  templateUrl: './ftp-service-dialog.component.html',
  styleUrls: ['./ftp-service-dialog.component.css'],
  standalone: false
})
export class FtpServiceDialogComponent extends BaseComponent implements OnInit, OnDestroy {

  readonly isEdit: boolean;
  readonly maxLength = FTP_FIELD_MAX_LENGTH;
  readonly FtpServerTypeUtil = FtpServerTypeUtil;
  readonly ftpServerTypes = [FtpServerType.FTP, FtpServerType.SFTP];

  form!: FormGroup;
  saving = false;
  testing = false;
  showPassword = false;
  revealingPassword = false;
  readonly timeoutLimits = FTP_CONNECTION_TIMEOUT;
  // Edit only: the stored password once the admin has explicitly revealed it. Kept
  // out of the form until then, and never logged; an unchanged revealed password
  // is sent as blank on save, which keeps the stored one.
  private revealedStoredPassword: string | null = null;
  serverError = '';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private configService: ConfigService,
    private dialogRef: MatDialogRef<FtpServiceDialogComponent, FtpServiceDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: FtpServiceDialogData,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService
  ) {
    super(errorLogger, matDialog);
    this.isEdit = !!data?.service;
  }

  ngOnInit(): void {
    const service = this.data?.service;
    const serverType = service?.ftpServerType ?? FtpServerType.SFTP;

    this.form = this.fb.group({
      ftpName: [service?.ftpName ?? '', [trimmedRequiredValidator, Validators.maxLength(FTP_FIELD_MAX_LENGTH.ftpName)]],
      isActive: [service ? service.isActive === 1 : true],
      ftpServerType: [serverType, Validators.required],
      ftpServerLocation: [service?.ftpServerLocation ?? '', [trimmedRequiredValidator, Validators.maxLength(FTP_FIELD_MAX_LENGTH.ftpServerLocation), ftpHostValidator]],
      controlPort: [service?.controlPort ?? FTP_DEFAULT_PORTS[serverType], [Validators.required, portValidator]],
      connectionTimeout: [service?.connectionTimeout ?? FTP_CONNECTION_TIMEOUT.default, [
        Validators.required, Validators.min(FTP_CONNECTION_TIMEOUT.min), Validators.max(FTP_CONNECTION_TIMEOUT.max), Validators.pattern(/^\d+$/)]],
      userName: [service?.userName ?? '', [trimmedRequiredValidator, Validators.maxLength(FTP_FIELD_MAX_LENGTH.userName), noInnerWhitespaceValidator]],
      // On edit the stored password is not loaded (the field shows dots); blank keeps it.
      // "Show password" fetches it on demand (togglePasswordVisibility).
      password: ['', this.isEdit
        ? [Validators.maxLength(FTP_FIELD_MAX_LENGTH.password)]
        : [Validators.required, Validators.maxLength(FTP_FIELD_MAX_LENGTH.password)]]
    });

    // Switching server type moves the port to the new type's default, unless the
    // Analyst has typed a custom port.
    let previousType: FtpServerType = serverType;
    this.subscriptions.add(
      this.form.get('ftpServerType')!.valueChanges.subscribe((type: FtpServerType) => {
        const port = this.form.get('controlPort')!;
        if (Number(port.value) === FTP_DEFAULT_PORTS[previousType]) {
          port.setValue(FTP_DEFAULT_PORTS[type]);
        }
        previousType = type;
      })
    );

    // disableClose keeps a stray backdrop click or Esc from silently discarding
    // edits; both are routed through cancel() so they still work, with a prompt.
    this.subscriptions.add(
      merge(
        this.dialogRef.backdropClick(),
        this.dialogRef.keydownEvents().pipe(filter(e => e.key === 'Escape'))
      ).subscribe(() => this.cancel())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get title(): string {
    return this.isEdit ? `Edit FTP Service — ${this.data.service!.ftpName}` : 'Add FTP Service';
  }

  /** First validation message for a control, or '' when valid/untouched. */
  errorFor(name: string): string {
    const control = this.form.get(name);
    if (!control || !control.errors || !(control.touched || control.dirty)) {
      return '';
    }
    const e = control.errors;
    const labels: Record<string, string> = {
      ftpName: 'Name', ftpServerType: 'Server type', ftpServerLocation: 'Server location',
      controlPort: 'Port', connectionTimeout: 'Connection timeout', userName: 'Username', password: 'Password'
    };
    if (e['server']) return e['server'];
    if (e['required']) return `${labels[name]} is required.`;
    if (e['maxlength']) return `${labels[name]} must be ${e['maxlength'].requiredLength} characters or fewer.`;
    if (e['ftpHost']) return e['ftpHost'];
    if (e['port']) return 'Port must be a whole number between 1 and 65535.';
    if (name === 'connectionTimeout' && (e['min'] || e['max'] || e['pattern'])) {
      return `Connection timeout must be a whole number of seconds between ${FTP_CONNECTION_TIMEOUT.min} and ${FTP_CONNECTION_TIMEOUT.max}.`;
    }
    if (e['whitespace']) return `${labels[name]} cannot contain spaces.`;
    return `${labels[name]} is invalid.`;
  }

  async save(): Promise<void> {
    if (this.saving) {
      return;
    }
    this.serverError = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.serverError = 'Please correct the highlighted fields.';
      return;
    }

    this.saving = true;
    this.form.disable({ emitEvent: false });
    try {
      const request = this.toRequest();
      const response = this.isEdit
        ? await this.organizationService.updateOrgFtpService(request)
        : await this.organizationService.insertOrgFtpService(request);
      this.dialogRef.close({ message: response?.message || 'FTP service saved successfully.' });
    } catch (err) {
      this.form.enable({ emitEvent: false });
      const apiError = toFtpApiError(err);
      this.serverError = apiError.message;
      for (const [name, message] of Object.entries(apiError.fieldErrors)) {
        const control = this.form.get(name);
        if (control) {
          control.setErrors({ ...(control.errors ?? {}), server: message });
          control.markAsTouched();
        }
      }
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    if (this.saving) {
      return;
    }
    if (!this.form.dirty) {
      this.dialogRef.close();
      return;
    }
    this.showConfirmDialog(
      'Discard changes?',
      'You have unsaved changes to this FTP service. Discard them?',
      confirmed => { if (confirmed) { this.dialogRef.close(); } },
      'Discard'
    );
  }

  /**
   * Show/Hide password. On edit, the first "show" with the field untouched fetches
   * the stored password (admin-only endpoint) and reveals it; it is never loaded
   * otherwise.
   */
  async togglePasswordVisibility(): Promise<void> {
    if (this.showPassword) {
      this.showPassword = false;
      return;
    }
    const control = this.form.get('password')!;
    if (this.isEdit && !control.value && this.revealedStoredPassword === null) {
      this.revealingPassword = true;
      try {
        this.revealedStoredPassword = await this.organizationService.getOrgFtpServicePassword(this.data.service!.id);
        control.setValue(this.revealedStoredPassword, { emitEvent: false });
      } catch (err) {
        this.serverError = toFtpApiError(err).message;
        return;
      } finally {
        this.revealingPassword = false;
      }
    }
    this.showPassword = true;
  }

  async testConnection(): Promise<void> {
    const required = ['ftpServerType', 'ftpServerLocation', 'controlPort', 'connectionTimeout', 'userName', 'password'];
    required.forEach(name => this.form.get(name)!.markAsTouched());
    if (required.some(name => this.form.get(name)!.invalid)) {
      return;
    }
    const value = this.form.getRawValue();

    this.serverError = '';
    this.testing = true;
    const busy = this.showBusyDialog('Testing FTP connection...');
    try {
      // Editing with the password untouched tests with the saved password, fetched
      // on demand and used only for this request.
      const password = value.password || (this.isEdit
        ? await this.organizationService.getOrgFtpServicePassword(this.data.service!.id)
        : '');
      const request = {
        ...new FtpService(value.ftpServerType, value.ftpServerLocation.trim(), value.userName.trim(), password, Number(value.controlPort)),
        // The configured timeout, enforced by the engine on the connect.
        connectionTimeout: Number(value.connectionTimeout)
      };
      const result = await this.configService.testFTPConnection(request as FtpService);
      if (result?.connectionstatus === 'Success') {
        this.displayInfo('Connection succeeded.', 'Test Connection');
      } else {
        this.displayError(result?.errormsg || 'The connection test failed.', 'Test Connection');
      }
    } catch (err) {
      const message = toFtpApiError(err).message;
      this.displayError(message || 'The connection test could not be run. Please try again.', 'Test Connection');
    } finally {
      busy.close();
      this.testing = false;
    }
  }

  private toRequest(): OrgFtpServiceSaveRequest {
    const value = this.form.getRawValue();
    return {
      id: this.data?.service?.id,
      ftpName: value.ftpName.trim(),
      ftpServerType: value.ftpServerType,
      ftpServerLocation: value.ftpServerLocation.trim(),
      controlPort: Number(value.controlPort),
      connectionTimeout: Number(value.connectionTimeout),
      userName: value.userName.trim(),
      // Blank keeps the stored password (also when it was only revealed, not changed).
      password: value.password && value.password !== this.revealedStoredPassword ? value.password : '',
      isActive: value.isActive ? 1 : 0
    };
  }
}
